
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAiAssistant } from '@/contexts/AiAssistantContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Archive } from 'lucide-react';

interface DeleteSessionDialogProps {
  sessionId: string;
  sessionName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DeleteSessionDialog: React.FC<DeleteSessionDialogProps> = ({
  sessionId,
  sessionName,
  isOpen,
  onOpenChange,
}) => {
  const { deleteSession } = useAiAssistant();
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const success = await deleteSession(sessionId);
      if (success) {
        toast.success('Session removed from list');
        onOpenChange(false);
      } else {
        toast.error('Failed to remove session');
      }
    } catch (error) {
      console.error('Error removing session:', error);
      toast.error('Failed to remove session');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-background/95 backdrop-blur-xl border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Session</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            Are you sure you want to remove "{sessionName}" from your list? 
            <div className="mt-2 flex items-center gap-2 text-sm">
              <Archive className="h-4 w-4" />
              <span>Messages with feedback will be preserved for analysis.</span>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel 
            className="border-white/10"
            disabled={isDeleting}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            disabled={isDeleting}
          >
            {isDeleting ? 'Removing...' : 'Remove'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
